import React, { useCallback, useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import type { ChartRequest } from 'app-components/flux/ai-chat/aiChatTypes';

import StdDialog from 'components/Dialogs/StdDialog';
import ChartPreview from 'components/AIChat/chartPreviews/ChartPreview';

type ChartPreviewDialogProps = {
  chart: ChartRequest;
  open: boolean;
  onClose: () => void;
};

const ChartPreviewDialog = ({ chart, open, onClose }: ChartPreviewDialogProps): React.JSX.Element => {
  const handleClose = () => onClose();
  const [dialogSize, setDialogSize] = useState(420);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ x: 0, y: 0, size: 420 });
  const minDialogSize = 320;

  const handleResizeMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      resizeStartRef.current = { x: event.clientX, y: event.clientY, size: dialogSize };
      setIsResizing(true);
    },
    [dialogSize],
  );

  const handleResizeMouseMove = useCallback(
    (event: MouseEvent) => {
      const { x, y, size } = resizeStartRef.current;
      const dx = event.clientX - x;
      const dy = event.clientY - y;
      const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
      const maxDialogSize = Math.max(
        minDialogSize,
        Math.floor(Math.min(window.innerWidth * 0.9, window.innerHeight * 0.9)),
      );
      const nextSize = Math.min(maxDialogSize, Math.max(minDialogSize, size + delta));

      setDialogSize((prev) => (prev === nextSize ? prev : nextSize));
    },
    [minDialogSize],
  );

  const handleResizeMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    window.addEventListener('mousemove', handleResizeMouseMove);
    window.addEventListener('mouseup', handleResizeMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleResizeMouseMove);
      window.removeEventListener('mouseup', handleResizeMouseUp);
    };
  }, [handleResizeMouseMove, handleResizeMouseUp, isResizing]);

  return (
    <StdDialog
      open={open}
      onClose={handleClose}
      size='custom'
      fullWidth={false}
      maxWidth={false}
      title={chart.title || 'Chart preview'}
      hideBackdrop
      disableBackdropClick
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      disableScrollLock
      aria-modal={false}
      sx={{
        pointerEvents: 'none',
        '& .MuiDialog-container': { pointerEvents: 'none' },
        '& .MuiPaper-root': { pointerEvents: 'auto' },
      }}
      paperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          width: dialogSize,
          height: dialogSize,
          minWidth: minDialogSize,
          minHeight: minDialogSize,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'hidden',
          pointerEvents: 'auto',
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {chart.description && (
          <Typography variant='captionRegular' color='textSecondary' gutterBottom>
            {chart.description}
          </Typography>
        )}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ChartPreview chart={chart} height='100%' width='100%' />
        </Box>
        <Box
          aria-hidden
          onMouseDown={handleResizeMouseDown}
          sx={(theme) => ({
            position: 'absolute',
            right: 6,
            bottom: 6,
            width: 16,
            height: 16,
            cursor: 'nwse-resize',
            zIndex: 2,
            borderRight: `2px solid ${theme.palette.divider}`,
            borderBottom: `2px solid ${theme.palette.divider}`,
            borderRadius: 2,
          })}
        />
      </DialogContent>
    </StdDialog>
  );
};

export default ChartPreviewDialog;
